import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal, Typography, Space } from 'antd';
import { CloudDownloadOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

/**
 * 备份提醒组件
 * 用于提醒用户定期备份数据，防止数据丢失
 */
const BackupReminder = ({ onExport }) => {
  // 控制提醒弹窗的显示
  const [modalVisible, setModalVisible] = useState(false);
  // 控制提醒横幅的显示
  const [bannerVisible, setBannerVisible] = useState(false);
  // 上次提醒时间
  const [lastReminder, setLastReminder] = useState(null);
  // 首次使用时间
  const [firstUseTime, setFirstUseTime] = useState(null);
  // 是否已经提醒过
  const [hasReminded, setHasReminded] = useState(false);

  // 初始化时间记录
  useEffect(() => {
    // 从本地存储获取首次使用时间
    const storedFirstUseTime = localStorage.getItem('firstUseTime');
    // 从本地存储获取上次提醒时间
    const storedLastReminder = localStorage.getItem('lastBackupReminder');
    // 从本地存储获取是否已经提醒过
    const storedHasReminded = localStorage.getItem('hasBackupReminded');
    
    // 如果没有记录首次使用时间，则记录当前时间
    if (!storedFirstUseTime) {
      const now = new Date().getTime();
      localStorage.setItem('firstUseTime', now.toString());
      setFirstUseTime(now);
    } else {
      setFirstUseTime(parseInt(storedFirstUseTime));
    }
    
    // 如果有上次提醒记录，则设置上次提醒时间
    if (storedLastReminder) {
      setLastReminder(parseInt(storedLastReminder));
    }
    
    // 如果有提醒标记，则设置已提醒状态
    if (storedHasReminded) {
      setHasReminded(storedHasReminded === 'true');
    }
  }, []);

  // 检查是否需要显示提醒
  useEffect(() => {
    const checkReminderStatus = () => {
      const now = new Date().getTime();
      
      // 如果有首次使用时间记录
      if (firstUseTime) {
        // 计算使用天数
        const daysSinceFirstUse = Math.floor((now - firstUseTime) / (1000 * 60 * 60 * 24));
        
        // 如果使用超过7天且未提醒过，显示弹窗提醒
        if (daysSinceFirstUse >= 7 && !hasReminded) {
          setModalVisible(true);
          setHasReminded(true);
          localStorage.setItem('hasBackupReminded', 'true');
          localStorage.setItem('lastBackupReminder', now.toString());
          setLastReminder(now);
          return;
        }
        
        // 如果已经提醒过，检查上次提醒是否超过30天
        if (hasReminded && lastReminder) {
          const daysSinceLastReminder = Math.floor((now - lastReminder) / (1000 * 60 * 60 * 24));
          
          // 如果上次提醒超过30天，显示横幅提醒
          if (daysSinceLastReminder >= 30) {
            setBannerVisible(true);
            localStorage.setItem('lastBackupReminder', now.toString());
            setLastReminder(now);
          }
        }
      }
    };
    
    // 执行检查
    checkReminderStatus();
  }, [firstUseTime, lastReminder, hasReminded]);

  // 处理导出操作
  const handleExport = () => {
    // 关闭弹窗
    setModalVisible(false);
    setBannerVisible(false);
    
    // 调用导出函数
    if (onExport) {
      onExport();
    }
    
    // 更新上次提醒时间
    const now = new Date().getTime();
    localStorage.setItem('lastBackupReminder', now.toString());
    setLastReminder(now);
  };

  // 关闭横幅
  const closeBanner = () => {
    setBannerVisible(false);
  };

  return (
    <>
      {/* 提醒横幅 */}
      {bannerVisible && (
        <Alert
          message={
            <div className="flex items-center justify-between w-full">
              <Space>
                <InfoCircleOutlined />
                <Text>您已超过30天未备份数据，建议定期备份以防数据丢失</Text>
              </Space>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<CloudDownloadOutlined />}
                  onClick={handleExport}
                  className="btn-modern-primary"
                >
                  立即备份
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={closeBanner}
                />
              </Space>
            </div>
          }
          type="info"
          className="mb-4"
          showIcon={false}
        />
      )}

      {/* 提醒弹窗 */}
      <Modal
        title="数据备份提醒"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            稍后提醒
          </Button>,
          <Button
            key="export"
            type="primary"
            icon={<CloudDownloadOutlined />}
            onClick={handleExport}
            className="btn-modern-primary"
          >
            立即备份
          </Button>
        ]}
        width={480}
        className="modal-modern"
      >
        <div className="py-4">
          <Paragraph>
            <InfoCircleOutlined className="text-[var(--apple-accent)] mr-2" />
            <Text strong>您已使用PromptPro超过一周，建议备份您的数据。</Text>
          </Paragraph>
          <Paragraph className="mt-4">
            由于PromptPro将数据存储在本地浏览器中，为防止数据丢失，我们强烈建议您定期导出备份。
          </Paragraph>
          <Paragraph className="mt-2">
            <ul className="list-disc pl-5">
              <li>导出的备份文件可以在需要时重新导入</li>
              <li>备份包含您的所有提示词、文件夹和标签</li>
              <li>建议至少每月备份一次数据</li>
            </ul>
          </Paragraph>
        </div>
      </Modal>
    </>
  );
};

export default BackupReminder;
